import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { detectLocale, loadLocale, setRuntimeConfig } from '@zagru/platform';
import { isMockMode, restoreMockSession } from './app/mockAuth';
import { App } from './app/App';
import { preloadShellRoutes } from './app/routeTree';
import { localeStore } from './app/state/appStores';
import './styles.css';

async function bootstrap(): Promise<void> {
  // C7: the catalogues are their own chunks — English (the fallback) and the language this viewer last chose (or the
  // browser's), fetched alongside the mock worker; another language loads when it is picked.
  const catalogues = Promise.all([loadLocale('en'), loadLocale(localeStore.getState().chosen ?? detectLocale())]);
  /*
   * F-01: the grid's rich cells (next-action button, route, place, ETA, reference) are registered before the first
   * render, explicitly — a module-level registration in the design system would be tree-shaken away and the cells
   * fall back to plain text. They are their own chunk (entry ≤ 280 KB gz), fetched alongside the catalogues.
   */
  const cells = import('@zagru/design-system/cells').then((m) => m.registerDomainCellRenderers());
  // The signed-in shell's screens are patched into the router on the first navigation that needs them; fetch them now.
  preloadShellRoutes().catch(() => undefined);
  if (isMockMode()) {
    setRuntimeConfig({ mockApi: true });
    // Dynamic import keeps msw out of production bundles.
    const { startMockApi } = await import('./mocks/browser');
    await startMockApi();
    restoreMockSession();
  }
  await Promise.all([catalogues, cells]);
  const container = document.getElementById('root');
  if (!container) throw new Error('Missing #root element');
  createRoot(container).render(
    <StrictMode>
      <App />
    </StrictMode>,
  );
}

void bootstrap();
